import { PipelineRunDto } from '../../types/pipelineFlow';
import { EmptyState } from '../common';
import { PipelineRunCard } from './PipelineRunCard';

interface PipelineRunsListProps {
  filteredRuns: PipelineRunDto[];
  totalRuns: number;
  hasActiveFilters: boolean;
  expandedRuns: Set<number>;
  limit: number;
  gitHubRepository?: string;
  onToggleRunDetails: (buildId: number) => void;
  onClearFilters: () => void;
  onRefresh: () => void;
  onLimitChange: (newLimit: number) => void;
}

export function PipelineRunsList({
  filteredRuns,
  totalRuns,
  hasActiveFilters,
  expandedRuns,
  limit,
  gitHubRepository,
  onToggleRunDetails,
  onClearFilters,
  onRefresh,
  onLimitChange,
}: PipelineRunsListProps) {
  const handleLimitChange = (newLimit: number) => {
    if (newLimit >= 1 && newLimit <= 100) {
      onLimitChange(newLimit);
    }
  };

  return (
    <div className="mt-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h3 className="text-lg font-semibold leading-6 text-gray-900">Recent Runs</h3>
          <p className="mt-2 text-sm text-gray-700">
            Latest {limit} pipeline executions for this pipeline.
          </p>
        </div>
        
        {/* Pagination Controls */}
        <div className="mt-4 sm:mt-0 sm:ml-4">
          <div className="flex items-center space-x-3">
            <label htmlFor="run-limit" className="text-sm font-medium text-gray-700">
              Show:
            </label>
            <div className="flex items-center space-x-2">
              <input
                id="run-limit"
                type="number"
                min="1"
                max="100"
                value={limit}
                onChange={(e) => {
                  const newLimit = parseInt(e.target.value) || 20;
                  handleLimitChange(newLimit);
                }}
                className="form-input w-16 text-center"
              />
              <span className="text-sm text-gray-500">runs</span>
            </div>
            
            {/* Preset buttons */}
            <div className="flex space-x-1">
              {[10, 20, 50].map((presetLimit) => (
                <button
                  key={presetLimit}
                  onClick={() => handleLimitChange(presetLimit)}
                  className={`px-2 py-1 text-xs rounded ${
                    limit === presetLimit
                      ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {presetLimit}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {/* Display filtered results info */}
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {hasActiveFilters ? (
              <span>
                Showing {filteredRuns.length} of {totalRuns} runs
                <span className="text-blue-600 font-medium ml-1">(filtered)</span>
              </span>
            ) : (
              <span>Showing {totalRuns} runs (excluding PR builds by default)</span>
            )}
          </div>
        </div>
        
        {filteredRuns.length === 0 ? (
          <EmptyState
            title="No runs match your filters"
            description={hasActiveFilters ? "Try adjusting your filters to see more results." : "Pipeline runs will appear here once the pipeline has been executed."}
            action={{
              label: hasActiveFilters ? "Clear Filters" : "Refresh Runs",
              onClick: hasActiveFilters ? onClearFilters : onRefresh
            }}
            icon={
              <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.348a1.125 1.125 0 01-1.667-.986V5.653z" />
              </svg>
            }
          />
        ) : (
          <div className="space-y-4">
            {filteredRuns.map((run: PipelineRunDto) => (
              <PipelineRunCard 
                key={run.id} 
                run={run} 
                gitHubRepository={gitHubRepository}
                isExpanded={expandedRuns.has(run.buildId)}
                onToggleDetails={() => onToggleRunDetails(run.buildId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}